import React from 'react';
import { motion } from 'framer-motion';

interface CustomerInfo {
  name: string;
  phone: string;
  email: string;
  specialRequests: string;
}

interface CustomerInfoFormProps {
  customerInfo: CustomerInfo;
  onChange: (info: CustomerInfo) => void;
}

export const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  customerInfo,
  onChange,
}) => {
  const handleChange = (field: keyof CustomerInfo) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({
        ...customerInfo,
        [field]: e.target.value,
      });
    };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="w-full"
    >
      <div className="space-y-5">
        {/* Name + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Name */}
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="flex flex-col"
          >
            <label
              htmlFor="customer-name"
              className="text-sm font-medium text-[var(--text-secondary)] mb-1"
            >
              Name <span className="text-red-500">*</span>
            </label>

            <input
              id="customer-name"
              name="name"
              type="text"
              required
              autoComplete="name"
              value={customerInfo.name}
              onChange={handleChange('name')}
              placeholder="Enter full name"
              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent transition-all"
            />
          </motion.div>

          {/* Phone */}
          <motion.div
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="flex flex-col"
          >
            <label
              htmlFor="customer-phone"
              className="text-sm font-medium text-[var(--text-secondary)] mb-1"
            >
              Phone <span className="text-red-500">*</span>
            </label>

            <input
              id="customer-phone"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              inputMode="tel"
              value={customerInfo.phone}
              onChange={handleChange('phone')}
              placeholder="Enter phone number"
              className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent transition-all"
            />
          </motion.div>
        </div>

        {/* Email */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="flex flex-col"
        >
          <label
            htmlFor="customer-email"
            className="text-sm font-medium text-[var(--text-secondary)] mb-1"
          >
            Email <span className="text-red-500">*</span>
          </label>

          <input
            id="customer-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={customerInfo.email}
            onChange={handleChange('email')}
            placeholder="Enter email address"
            className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent transition-all"
          />
        </motion.div>

        {/* Special Requests */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="flex flex-col"
        >
          <label
            htmlFor="customer-special-requests"
            className="text-sm font-medium text-[var(--text-secondary)] mb-1"
          >
            Special Requests{" "}
            <span className="text-[var(--text-muted)] text-xs">(Optional)</span>
          </label>

          <textarea
            id="customer-special-requests"
            name="specialRequests"
            rows={3}
            value={customerInfo.specialRequests}
            onChange={handleChange('specialRequests')}
            placeholder="Any special instructions or notes"
            className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent transition-all resize-y"
          />
        </motion.div>
      </div>
    </motion.div>
  );
};
